import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Maximize2, X } from "lucide-react";
import { Button, ResultCelebration, SmartImage, StepDots } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { QuizResult } from "@ec2/contracts";
import { useTRPC } from "../lib/api";
import { appConfig } from "../lib/config";
import { generalError, type LocalizedError } from "../lib/errors";
import { usePasskeyPrompt } from "../state/hooks";
import { GroupBonusView } from "../components/GroupBonusView";
import { Sheet } from "../components/Sheet";
import { ImageLightbox } from "../components/ImageLightbox";
import { PasskeyPrompt } from "../components/PasskeyPrompt";
import { ErrorLine, LoadingLine } from "../components/common";

/**
 * Quiz player (brief §6): one question per screen, dots + thin accent progress
 * bar. A tap only SELECTS an answer (outlined); the fixed Next / Submit
 * button at the bottom moves on — so a scroll that lands as a short tap
 * can't answer a question by accident. Pictures open full-screen. No
 * per-question reveal — score lands at the end. The pass mark and the
 * number of attempts are set per event on the quest (quests.passThreshold /
 * quests.maxAttempts); a failed attempt with tries left offers a retry.
 */
export function QuizScreen({ questId }: { questId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const quiz = useQuery({ ...trpc.quest.getQuiz.queryOptions({ questId }), retry: false, staleTime: Infinity });
  const submit = useMutation(trpc.quest.submitQuiz.mutationOptions());
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string[]>>({});
  const [result, setResult] = useState<QuizResult | null>(null);
  // §5: one celebration at a time — the group bonus follows the quiz's own.
  const [showGroupBonus, setShowGroupBonus] = useState(false);
  const [error, setError] = useState<LocalizedError | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [enlarged, setEnlarged] = useState<string | null>(null);
  const passkey = usePasskeyPrompt();
  const [showPasskey, setShowPasskey] = useState(false);

  if (quiz.isLoading) return <LoadingLine />;
  // The result screen must render before the query-error branch: after
  // submit we invalidate queries and getQuiz refetches into CONFLICT
  // (completed quizzes are no longer served) — the score still shows.
  if (result) {
    const scorePercent = Math.round(result.score * 100);
    if (showGroupBonus && result.groupBonus) {
      return (
        <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-6" data-testid="ec-quiz-result">
          <GroupBonusView bonus={result.groupBonus} onDone={() => void navigate({ to: "/quests" })} />
          {showPasskey && <PasskeyPrompt onDone={() => setShowPasskey(false)} />}
        </main>
      );
    }
    return (
      <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center px-6" data-testid="ec-quiz-result">
        <p
          className={`text-center font-display text-[44px] leading-none font-extrabold tabular-nums ${result.passed ? "text-fg" : "text-danger"}`}
        >
          {scorePercent}%
        </p>
        <ResultCelebration
          status={result.passed ? "solved" : "failed"}
          title={result.passed ? t("ec2.celebration.solvedToast") : t("ec2.celebration.failedTitle")}
          amount={result.passed ? result.bucksAwarded : undefined}
          amountLabel={t("ec2.celebration.earned", { amount: result.bucksAwarded })}
          message={
            result.passed
              ? undefined
              : result.attemptsLeft > 0
                ? t("ec2.quiz.failedRetry", {
                    correct: result.correctAnswers,
                    total: result.totalQuestions,
                    needed: result.requiredCorrect,
                    attemptsLeft: result.attemptsLeft,
                  })
                : t("ec2.quiz.failed", {
                    correct: result.correctAnswers,
                    total: result.totalQuestions,
                    needed: result.requiredCorrect,
                  })
          }
          actionLabel={t("ec2.celebration.backToQuests")}
          onAction={() => {
            if (result.passed && result.groupBonus) {
              setShowGroupBonus(true);
              return;
            }
            void navigate({ to: "/quests" });
          }}
        />
        {!result.passed && result.attemptsLeft > 0 && (
          <Button
            variant="primary"
            className="mt-4"
            data-testid="ec-quiz-retry"
            onClick={() => {
              // Reset to a clean run and refetch the play view, which the
              // server will now serve again because a try remains.
              setResult(null);
              setAnswers({});
              setIndex(0);
              void quiz.refetch();
            }}
          >
            {t("ec2.quiz.retry")}
          </Button>
        )}
        {showPasskey && <PasskeyPrompt onDone={() => setShowPasskey(false)} />}
      </main>
    );
  }

  if (quiz.isError || !quiz.data) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-4 px-6">
        <ErrorLine error={generalError(quiz.error)} className="text-base" />
        <Button variant="ghost" onClick={() => void navigate({ to: "/quests" })}>
          {t("ec2.celebration.backToQuests")}
        </Button>
      </main>
    );
  }

  const play = quiz.data;
  const total = play.questions.length;
  const question = play.questions[Math.min(index, total - 1)];

  const finish = async (finalAnswers: Record<string, string[]>) => {
    try {
      const submitted = await submit.mutateAsync({
        questId,
        answers: play.questions
          .filter((q) => (finalAnswers[q.id] ?? []).length > 0)
          .map((q) => ({ questionId: q.id, answerIds: finalAnswers[q.id]! })),
      });
      setResult(submitted);
      await queryClient.invalidateQueries();
      if (submitted.passed && passkey.shouldPrompt) {
        passkey.markPrompted();
        setShowPasskey(true);
      }
    } catch (err) {
      setError(generalError(err));
    }
  };

  const advance = (nextAnswers: Record<string, string[]>) => {
    if (index + 1 < total) {
      setIndex(index + 1);
    } else {
      void finish(nextAnswers);
    }
  };

  const choose = (answerId: string) => {
    if (!question) return;
    const current = answers[question.id] ?? [];
    let next: string[];
    if (question.multiSelect) {
      next = current.includes(answerId) ? current.filter((id) => id !== answerId) : [...current, answerId];
    } else {
      next = [answerId];
    }
    setAnswers({ ...answers, [question.id]: next });
  };

  const selected = question ? (answers[question.id] ?? []) : [];

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-4" data-testid="ec-quiz">
      <div className="flex items-center justify-between pt-4">
        <StepDots count={total} active={index} label={t("ec2.quiz.question", { current: index + 1, total })} />
        <button
          type="button"
          onClick={() => setConfirmLeave(true)}
          aria-label={t("ec2.quiz.leave")}
          data-testid="ec-quiz-leave"
          className="flex size-10 items-center justify-center rounded-full text-fg-secondary focus-visible:outline-2 focus-visible:outline-primary"
        >
          <X size={22} aria-hidden />
        </button>
      </div>
      <div aria-hidden className="mt-2 h-0.5 overflow-hidden rounded-full bg-surface-overlay">
        <div className="h-full bg-accent transition-[width]" style={{ width: `${(index / total) * 100}%` }} />
      </div>
      <p className="mt-3 text-[13px] text-fg-secondary" data-testid="ec-quiz-passmark">
        {t("ec2.quiz.passMark", { needed: play.requiredCorrect, total })}
        {play.attempt + play.attemptsLeft > 1 &&
          ` · ${t("ec2.quiz.attempt", { attempt: play.attempt, total: play.attempt + play.attemptsLeft })}`}
      </p>

      {question && (
        <>
          <h1 className="mt-8 font-display text-[30px] leading-[1.1] font-bold tracking-[-0.02em] text-fg">
            {question.text}
          </h1>
          {question.imageKey && (
            <button
              type="button"
              onClick={() => setEnlarged(question.imageKey)}
              aria-label={t("ec2.quiz.imageEnlarge")}
              data-testid="ec-quiz-image"
              className="relative mt-4 block w-full overflow-hidden rounded-(--radius-card) focus-visible:outline-2 focus-visible:outline-primary"
            >
              <SmartImage
                base={appConfig.mediaBase}
                mediaKey={question.imageKey}
                alt=""
                width={480}
                height={320}
                className="w-full object-cover"
              />
              <span
                aria-hidden
                className="absolute right-2 bottom-2 flex size-8 items-center justify-center rounded-full bg-black/55 text-white"
              >
                <Maximize2 size={16} />
              </span>
            </button>
          )}
          <div className="mt-6 mb-6 flex flex-col gap-3" role={question.multiSelect ? "group" : "radiogroup"} aria-label={question.text}>
            {question.answers.map((answer) => {
              const isSelected = selected.includes(answer.id);
              return (
                <div key={answer.id} className="flex items-stretch gap-2">
                  <button
                    type="button"
                    role={question.multiSelect ? "checkbox" : "radio"}
                    aria-checked={isSelected}
                    onClick={() => choose(answer.id)}
                    data-testid="ec-quiz-answer"
                    className={
                      `flex min-h-14 flex-1 items-center gap-3 rounded-(--radius-chip) border px-4 py-3 text-left text-base text-fg ` +
                      `focus-visible:outline-2 focus-visible:outline-primary ` +
                      (isSelected ? "border-2 border-primary bg-primary/8" : "border-border bg-surface")
                    }
                  >
                    <span
                      aria-hidden
                      className={`flex size-5 shrink-0 items-center justify-center border ${question.multiSelect ? "rounded-md" : "rounded-full"} ${isSelected ? "border-primary bg-primary text-primary-fg" : "border-border"}`}
                    >
                      {isSelected ? (question.multiSelect ? "✓" : <span className="size-2 rounded-full bg-primary-fg" />) : ""}
                    </span>
                    <span className="flex-1">{answer.text}</span>
                  </button>
                  {answer.imageKey && (
                    <button
                      type="button"
                      onClick={() => setEnlarged(answer.imageKey)}
                      aria-label={t("ec2.quiz.imageEnlargeAnswer", { answer: answer.text })}
                      data-testid="ec-quiz-answer-image"
                      className="shrink-0 overflow-hidden rounded-(--radius-chip) border border-border focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <SmartImage
                        base={appConfig.mediaBase}
                        mediaKey={answer.imageKey}
                        alt=""
                        width={56}
                        height={56}
                        maxVariant="thumb"
                        className="size-14 object-cover"
                      />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
      <ErrorLine error={error} className="mt-4" />
      {question && (
        // Pinned to the bottom: the only way forward, out of the scroll path.
        <div className="sticky bottom-0 -mx-4 mt-auto border-t border-border bg-surface px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
          <Button
            variant="primary"
            className="w-full"
            disabled={selected.length === 0 || submit.isPending}
            onClick={() => advance(answers)}
            data-testid="ec-quiz-next"
          >
            {index + 1 < total ? t("ec2.quiz.next") : t("ec2.quiz.submit")}
          </Button>
        </div>
      )}
      {enlarged && <ImageLightbox mediaKey={enlarged} onClose={() => setEnlarged(null)} />}

      {confirmLeave && (
        <Sheet label={t("ec2.quiz.leaveWarning")} onClose={() => setConfirmLeave(false)}>
          <div className="flex flex-col gap-4">
            <p className="pr-8 text-base text-fg">{t("ec2.quiz.leaveWarning")}</p>
            <div className="flex items-center justify-between gap-3">
              <Button variant="ghost" onClick={() => void navigate({ to: "/quests" })} data-testid="ec-quiz-leave-confirm">
                {t("ec2.quiz.leave")}
              </Button>
              <Button variant="primary" onClick={() => setConfirmLeave(false)}>
                {t("ec2.quiz.stay")}
              </Button>
            </div>
          </div>
        </Sheet>
      )}
    </main>
  );
}
